import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { formatMoney, formatQty, normalizeQty } from '../lib/intakeCatalog';
import {
  flattenInventorySnapshots,
  getInventoryFreshness,
  normalizeMorpheusInventoryDoc,
} from '../lib/morpheusInventoryBridge';

const inputStyle = {
  width: '100%',
  background: '#0b1727',
  color: '#e7f4ff',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 14,
  padding: '12px 14px',
  outline: 'none',
  boxSizing: 'border-box',
};

function blankForm() {
  return {
    name: '',
    sku: '',
    category: '',
    baseQty: '0.5',
    basePrice: '',
    priceRulesText: '',
    active: true,
  };
}

function parsePriceRules(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [qtyPart, pricePart] = line.split(':').map((part) => String(part || '').trim());
      return { qty: normalizeQty(qtyPart, 0), price: Number(pricePart || 0) };
    })
    .filter((rule) => Number.isFinite(rule.qty) && rule.qty > 0 && Number.isFinite(rule.price) && rule.price > 0)
    .sort((a, b) => a.qty - b.qty);
}

function rulesToText(rules) {
  return Array.isArray(rules)
    ? rules.map((rule) => `${formatQty(rule.qty)}:${Number(rule.price || 0)}`).join('\n')
    : '';
}

function getBestPriceLabel(product) {
  const baseQty = normalizeQty(product?.baseQty, 0.5);
  const basePrice = Number(product?.basePrice || 0);
  const rules = Array.isArray(product?.priceRules) ? [...product.priceRules] : [];
  rules.sort((a, b) => a.qty - b.qty);
  if (!rules.length) return `${formatMoney(basePrice)} for ${formatQty(baseQty)}`;
  const bestRule = rules.reduce((best, rule) => {
    const currentUnit = Number(rule.price || 0) / Math.max(Number(rule.qty || 0), 0.0001);
    if (!best) return { ...rule, unit: currentUnit };
    return currentUnit < best.unit ? { ...rule, unit: currentUnit } : best;
  }, null);
  return bestRule ? `${formatMoney(bestRule.price)} for ${formatQty(bestRule.qty)}` : `${formatMoney(basePrice)} for ${formatQty(baseQty)}`;
}

function PermissionHelp() {
  return (
    <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
      Stock on Hand is live, but this login does not currently have Firestore access to the Link/Morpheus collections.
      <div className="mt-2 text-amber-200/90">
        Allow this app to read <span className="font-semibold">orgs/&lbrace;orgId&rbrace;/morpheus</span> and manage <span className="font-semibold">ontimeProducts</span>.
      </div>
    </div>
  );
}

function FreshnessPill({ syncedAt }) {
  const freshness = getInventoryFreshness(syncedAt);
  const classes = {
    emerald: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/20',
    amber: 'bg-amber-500/15 text-amber-200 border-amber-400/20',
    rose: 'bg-rose-500/15 text-rose-200 border-rose-400/20',
    slate: 'bg-white/10 text-slate-300 border-white/10',
  };
  return <span className={`rounded-full border px-2.5 py-1 text-xs ${classes[freshness.tone] || classes.slate}`}>{freshness.label}</span>;
}

export default function OnTimeProductsPage() {
  const { claims } = useAuth();
  const orgId = claims?.orgId || '';

  const [products, setProducts] = useState([]);
  const [inventorySnapshots, setInventorySnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('ALL');
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(blankForm());
  const [permissionIssue, setPermissionIssue] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [expandedStockKey, setExpandedStockKey] = useState('');

  useEffect(() => {
    if (!orgId) {
      setProducts([]);
      setLoading(false);
      return undefined;
    }
    const ref = collection(db, 'orgs', orgId, 'ontimeProducts');
    const unsubscribe = onSnapshot(
      query(ref, orderBy('name')),
      (snap) => {
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPermissionIssue(false);
        setLoading(false);
      },
      (err) => {
        console.error('[OnTime Products] load failed', err);
        setPermissionIssue(true);
        setError('Missing or insufficient permissions.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      setInventorySnapshots([]);
      setInventoryLoading(false);
      return undefined;
    }
    const ref = collection(db, 'orgs', orgId, 'morpheus');
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const snapshots = snap.docs
          .map((d) => normalizeMorpheusInventoryDoc(d.id, d.data()))
          .filter(Boolean)
          .sort((a, b) => String(a.siteName).localeCompare(String(b.siteName)));
        setInventorySnapshots(snapshots);
        setInventoryLoading(false);
      },
      (err) => {
        console.error('[Link Stock on Hand] Morpheus inventory load failed', err);
        setPermissionIssue(true);
        setInventoryLoading(false);
      }
    );
    return () => unsubscribe();
  }, [orgId]);

  const inventoryRows = useMemo(() => flattenInventorySnapshots(inventorySnapshots), [inventorySnapshots]);
  const siteOptions = useMemo(() => {
    const map = new Map();
    inventorySnapshots.forEach((snapshot) => {
      const siteId = String(snapshot?.siteId || '').trim();
      const siteName = String(snapshot?.siteName || '').trim();
      const lowerId = siteId.toLowerCase();
      const lowerName = siteName.toLowerCase();
      if (!siteId || lowerId === 'all' || lowerId === 'all sites' || lowerId === 'all stores' || lowerName === 'all sites' || lowerName === 'all stores') return;
      map.set(siteId, siteName || siteId);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [inventorySnapshots]);

  function stockKey(row = {}) {
    return String(row.sku || row.productName || '').trim().toLowerCase();
  }

  const filteredInventory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = inventoryRows.filter((row) => {
      if (siteFilter !== 'ALL' && row.siteId !== siteFilter) return false;
      if (!q) return true;
      return [row.productName, row.sku, row.category, row.siteName].join(' ').toLowerCase().includes(q);
    });

    if (siteFilter !== 'ALL') {
      return rows.sort((a, b) => String(a.category || 'Other').localeCompare(String(b.category || 'Other')) || String(a.productName).localeCompare(String(b.productName)));
    }

    const grouped = new Map();
    rows.forEach((row) => {
      const key = stockKey(row);
      if (!key) return;
      const current = grouped.get(key) || {
        ...row,
        id: key,
        stockKey: key,
        physicalSoh: 0,
        siteCount: 0,
        inStockSites: 0,
        siteBreakdown: [],
        syncedAt: row.syncedAt,
      };
      const soh = Number(row.physicalSoh || 0);
      current.physicalSoh += soh;
      current.siteCount += 1;
      if (soh > 0) current.inStockSites += 1;
      current.siteBreakdown.push(row);
      if (Date.parse(row.syncedAt || '') > Date.parse(current.syncedAt || '')) current.syncedAt = row.syncedAt;
      grouped.set(key, current);
    });

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        siteLabel: `${row.inStockSites} in stock / ${row.siteCount} synced`,
        siteBreakdown: row.siteBreakdown.sort((a, b) => String(a.siteName).localeCompare(String(b.siteName))),
      }))
      .sort((a, b) => String(a.category || 'Other').localeCompare(String(b.category || 'Other')) || String(a.productName).localeCompare(String(b.productName)));
  }, [inventoryRows, search, siteFilter]);

  const groupedInventory = useMemo(() => {
    const groups = [];
    const map = new Map();
    filteredInventory.forEach((row) => {
      const category = String(row.category || 'Other').trim() || 'Other';
      if (!map.has(category)) {
        const group = { category, rows: [] };
        map.set(category, group);
        groups.push(group);
      }
      map.get(category).rows.push(row);
    });
    return groups;
  }, [filteredInventory]);

  const totals = useMemo(() => {
    const totalProducts = new Set(inventoryRows.map((row) => row.sku || row.productName)).size;
    const totalUnits = inventoryRows.reduce((sum, row) => sum + Number(row.physicalSoh || 0), 0);
    const lowStock = inventoryRows.filter((row) => Number(row.physicalSoh || 0) <= 2).length;
    const outOfStock = inventoryRows.filter((row) => Number(row.physicalSoh || 0) <= 0).length;
    return { totalProducts, totalUnits, lowStock, outOfStock };
  }, [inventoryRows]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((product) => [product.name, product.sku, product.category].join(' ').toLowerCase().includes(q));
  }, [products, search]);

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function resetForm() {
    setForm(blankForm());
    setEditingId('');
  }

  function editProduct(product) {
    setEditingId(product.id);
    setForm({
      name: product.name || '',
      sku: product.sku || '',
      category: product.category || '',
      baseQty: String(product.baseQty ?? '0.5'),
      basePrice: String(product.basePrice ?? ''),
      priceRulesText: rulesToText(product.priceRules),
      active: product.active !== false,
    });
    setCatalogOpen(true);
    setMessage('');
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!orgId) return setError('Missing orgId on this user.');
    if (!form.name.trim()) return setError('Product name is required.');
    const baseQty = normalizeQty(form.baseQty, 0);
    if (!Number.isFinite(baseQty) || baseQty <= 0) return setError('Starting quantity must be more than 0.');
    const basePrice = Number(form.basePrice || 0);
    if (!Number.isFinite(basePrice) || basePrice < 0) return setError('Base price must be a valid number.');

    const payload = {
      name: form.name.trim(),
      sku: form.sku.trim(),
      category: form.category.trim(),
      baseQty,
      basePrice,
      priceRules: parsePriceRules(form.priceRulesText),
      active: form.active !== false,
      updatedAt: serverTimestamp(),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'orgs', orgId, 'ontimeProducts', editingId), payload);
        setMessage(`Updated ${payload.name}.`);
      } else {
        await addDoc(collection(db, 'orgs', orgId, 'ontimeProducts'), { ...payload, createdAt: serverTimestamp() });
        setMessage(`Created ${payload.name}.`);
      }
      resetForm();
    } catch (err) {
      console.error('[OnTime Products] save failed', err);
      setPermissionIssue(String(err?.message || '').toLowerCase().includes('permission'));
      setError(err?.message || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(product) {
    if (!orgId) return;
    setMessage('');
    setError('');
    try {
      await updateDoc(doc(db, 'orgs', orgId, 'ontimeProducts', product.id), {
        active: product.active === false,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('[OnTime Products] toggle failed', err);
      setPermissionIssue(String(err?.message || '').toLowerCase().includes('permission'));
      setError(err?.message || 'Failed to update product status.');
    }
  }

  const activeCount = products.filter((product) => product.active !== false).length;
  const basePreviewQty = normalizeQty(form.baseQty, 0.5);
  const basePreviewPrice = Number(form.basePrice || 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white">Stock on Hand</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-400">
            Link reads Morpheus completed count sessions first. Prepared/POS snapshots are only a fallback until live movement projection is switched on.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setCatalogOpen((v) => !v)} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200 hover:bg-white/10">
            {catalogOpen ? 'Hide Link catalog' : 'Manage Link catalog'}
          </button>
        </div>
      </div>

      {message ? <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">{message}</div> : null}
      {permissionIssue ? <PermissionHelp /> : null}
      {error && !permissionIssue ? <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</div> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Stores synced</div>
          <div className="mt-2 text-3xl font-semibold text-white">{siteOptions.length}</div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Unique products</div>
          <div className="mt-2 text-3xl font-semibold text-white">{totals.totalProducts}</div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Units on hand</div>
          <div className="mt-2 text-3xl font-semibold text-white">{totals.totalUnits.toLocaleString()}</div>
        </div>
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Attention</div>
          <div className="mt-2 text-3xl font-semibold text-white">{totals.lowStock}</div>
          <div className="mt-1 text-xs text-slate-400">{totals.outOfStock} out of stock</div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Morpheus inventory feed</div>
            <div className="mt-1 text-lg font-semibold text-white">{filteredInventory.length} visible stock rows</div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <select style={{ ...inputStyle, minWidth: 220 }} value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
              <option value="ALL">All Sites</option>
              {siteOptions.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
            </select>
            <input style={{ ...inputStyle, minWidth: 280 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product, sku, category or site" />
          </div>
        </div>

        {inventoryLoading ? (
          <div className="rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-5 text-sm text-slate-400">Loading Morpheus stock snapshots...</div>
        ) : filteredInventory.length ? (
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <div className="max-h-[620px] overflow-auto">
              <table className="min-w-full divide-y divide-white/10 text-sm">
                <thead className="sticky top-0 bg-[#0b1727] text-xs uppercase tracking-[0.18em] text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Product</th>
                    <th className="px-4 py-3 text-left">{siteFilter === 'ALL' ? 'Sites' : 'Site'}</th>
                    <th className="px-4 py-3 text-left">Category</th>
                    <th className="px-4 py-3 text-right">SOH</th>
                    <th className="px-4 py-3 text-right">Retail</th>
                    <th className="px-4 py-3 text-left">Freshness</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 bg-[#07111f]">
                  {groupedInventory.map((group) => (
                    <React.Fragment key={group.category}>
                      <tr className="bg-cyan-500/10">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-base font-semibold text-white">{group.category}</div>
                              <div className="text-xs text-cyan-200">{group.rows.length} products</div>
                            </div>
                            <div className="text-sm font-semibold text-slate-200">
                              {group.rows.reduce((sum, row) => sum + Number(row.physicalSoh || 0), 0).toLocaleString()} SOH
                            </div>
                          </div>
                        </td>
                      </tr>
                      {group.rows.map((row) => {
                        const key = `${siteFilter}-${row.stockKey || row.siteId}-${row.id}-${row.productName}`;
                        const isExpanded = expandedStockKey === key;
                        const canExpand = siteFilter === 'ALL' && Array.isArray(row.siteBreakdown) && row.siteBreakdown.length > 0;
                        return (
                          <React.Fragment key={key}>
                            <tr className="hover:bg-white/[0.03]">
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => canExpand && setExpandedStockKey(isExpanded ? '' : key)}
                                  className={`text-left ${canExpand ? 'cursor-pointer' : 'cursor-default'}`}
                                >
                                  <div className="font-medium text-white">{row.productName}</div>
                                  <div className="text-xs text-slate-500">{row.sku || 'No SKU'}{canExpand ? ' • tap for site stock' : ''}</div>
                                </button>
                              </td>
                              <td className="px-4 py-3 text-slate-300">{siteFilter === 'ALL' ? row.siteLabel : row.siteName}</td>
                              <td className="px-4 py-3 text-slate-400">{row.category || 'Uncategorised'}</td>
                              <td className={`px-4 py-3 text-right font-semibold ${Number(row.physicalSoh || 0) <= 0 ? 'text-rose-200' : Number(row.physicalSoh || 0) <= 2 ? 'text-amber-200' : 'text-emerald-200'}`}>
                                {Number(row.physicalSoh || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-right text-slate-300">{row.retailPrice ? formatMoney(row.retailPrice) : '—'}</td>
                              <td className="px-4 py-3"><FreshnessPill syncedAt={row.syncedAt} /></td>
                            </tr>
                            {canExpand && isExpanded ? (
                              <tr className="bg-white/[0.02]">
                                <td colSpan={6} className="px-4 py-3">
                                  <div className="rounded-2xl border border-cyan-400/15 bg-cyan-500/5 p-3">
                                    <div className="mb-2 text-xs uppercase tracking-[0.18em] text-cyan-300">Stock by Site</div>
                                    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                                      {row.siteBreakdown.map((siteRow) => (
                                        <div key={`${siteRow.siteId}-${siteRow.sku}-${siteRow.productName}`} className="rounded-xl border border-white/10 bg-[#07111f] px-3 py-2">
                                          <div className="text-sm font-semibold text-white">{siteRow.siteName}</div>
                                          <div className={`text-lg font-semibold ${Number(siteRow.physicalSoh || 0) <= 0 ? 'text-rose-200' : Number(siteRow.physicalSoh || 0) <= 2 ? 'text-amber-200' : 'text-emerald-200'}`}>{Number(siteRow.physicalSoh || 0).toLocaleString()} SOH</div>
                                          <div className="mt-1"><FreshnessPill syncedAt={siteRow.syncedAt} /></div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-5 text-sm text-slate-400">
            No Morpheus stock snapshots found yet. Open Morpheus, import/sync POS or finalize prepared inventory, and the snapshot will publish for Link.
          </div>
        )}
      </div>

      {catalogOpen ? (
        <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Manual Link catalog</div>
                <div className="mt-1 text-lg font-semibold text-white">{editingId ? 'Edit product' : 'New product'}</div>
              </div>
              {editingId ? <button type="button" onClick={resetForm} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-300">Cancel</button> : null}
            </div>
            <form className="space-y-3" onSubmit={handleSubmit}>
              <input style={inputStyle} value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Product name" />
              <div className="grid gap-3 md:grid-cols-2">
                <input style={inputStyle} value={form.sku} onChange={(e) => setField('sku', e.target.value)} placeholder="SKU" />
                <input style={inputStyle} value={form.category} onChange={(e) => setField('category', e.target.value)} placeholder="Category" />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <input style={inputStyle} value={form.baseQty} onChange={(e) => setField('baseQty', e.target.value)} placeholder="Starting qty e.g. 0.5" inputMode="decimal" />
                <input style={inputStyle} value={form.basePrice} onChange={(e) => setField('basePrice', e.target.value)} placeholder="Base price" inputMode="decimal" />
              </div>
              <textarea style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }} value={form.priceRulesText} onChange={(e) => setField('priceRulesText', e.target.value)} placeholder={'Pricing rules, one per line\n0.5:75\n1:140\n2:260'} />
              <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-300">
                <input type="checkbox" checked={form.active !== false} onChange={(e) => setField('active', e.target.checked)} /> Product active in intake search
              </label>
              <div className="rounded-2xl border border-cyan-400/15 bg-cyan-500/5 px-4 py-3 text-sm text-slate-300">
                <div className="text-xs uppercase tracking-[0.2em] text-cyan-300">Base preview</div>
                <div className="mt-1 text-white">{basePreviewPrice > 0 ? `${formatMoney(basePreviewPrice)} for ${formatQty(basePreviewQty)}` : 'Enter starting qty and base price'}</div>
              </div>
              <button type="submit" disabled={saving} className="w-full rounded-2xl bg-cyan-500/20 px-4 py-3 font-medium text-cyan-100 ring-1 ring-cyan-400/20 transition hover:bg-cyan-500/30 disabled:cursor-not-allowed disabled:opacity-60">
                {saving ? 'Saving...' : editingId ? 'Save changes' : 'Create product'}
              </button>
            </form>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
            <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500">Live intake catalog</div>
                <div className="mt-1 text-lg font-semibold text-white">{activeCount} active / {products.length} total</div>
              </div>
            </div>
            {loading ? (
              <div className="rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-5 text-sm text-slate-400">Loading products...</div>
            ) : filteredProducts.length ? (
              <div className="space-y-3">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="rounded-2xl border border-white/10 bg-[#0b1727] p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-lg font-semibold text-white">{product.name}</div>
                          <span className={`rounded-full px-2.5 py-1 text-xs ${product.active !== false ? 'bg-emerald-500/15 text-emerald-200' : 'bg-white/10 text-slate-400'}`}>{product.active !== false ? 'Active' : 'Inactive'}</span>
                        </div>
                        <div className="mt-1 text-sm text-slate-400">{[product.sku, product.category].filter(Boolean).join(' • ') || 'No sku/category yet'}</div>
                      </div>
                      <div className="text-left lg:text-right">
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Best price</div>
                        <div className="mt-1 text-lg font-semibold text-cyan-200">{getBestPriceLabel(product)}</div>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" onClick={() => editProduct(product)} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200 hover:bg-white/10">Edit</button>
                      <button type="button" onClick={() => toggleActive(product)} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200 hover:bg-white/10">{product.active !== false ? 'Deactivate' : 'Activate'}</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-5 text-sm text-slate-400">No manual Link products yet.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
